'use client';

import { type ReactNode, useEffect } from 'react';
import {
    PromoBar,
    type PromoBarsResponseType,
} from '@paypoteam/page-components';
import { ToastProvider } from '@paypoteam/ui-components';
import * as Sentry from '@sentry/nextjs';
import type { ImageProps } from 'next/image';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';

import { bannerClickEvent } from '@/lib/gtm';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';
import { footerId, mainId } from '@/utils/sectionIds';

import SkipLink from '@/components/atoms/SkipLink';
import Footer from '@/components/organisms/Footer';
import type { MobileAppBarProps } from '@/components/organisms/MobileAppBar';
import { MobileAppBar } from '@/components/organisms/MobileAppBar';
import {
    Navigation,
    type NavigationConfigType,
} from '@/components/organisms/Navigation';
import { Header } from '@/components/layout/Header';

import { env } from '@/env.mjs';

export interface PageLayoutProps {
    children: ReactNode;
    promoBarData?: PromoBarsResponseType;
    mobileAppBarConfig?: MobileAppBarProps['config'];
    navigationConfig: NavigationConfigType;
}

export default function PageLayout({
    children,
    promoBarData,
    mobileAppBarConfig,
    navigationConfig,
}: PageLayoutProps) {
    const location = usePathname();
    const searchParams = useSearchParams();
    const { minWidthMd } = useCssDimensions();

    Sentry.withScope(scope => scope.setTag('page-pathname', location));

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const id = hash.substring(1);
            const element = document.getElementById(id);
            if (element) {
                element.focus();
            }
            window.history.pushState(
                {},
                document.title,
                window.location.pathname,
            );
        }
    }, [location, searchParams]);

    return (
        <>
            <ToastProvider />

            <Header>
                <nav
                    aria-label={
                        env.NEXT_PUBLIC_APP_COUNTRY === 'RO'
                            ? 'Navigare linkuri de sărit'
                            : 'Nawigacja linków pomijających'
                    }
                >
                    <ul>
                        <li>
                            <SkipLink href={`#${mainId}`}>
                                {env.NEXT_PUBLIC_APP_COUNTRY === 'RO'
                                    ? 'Mergi la conținutul principal'
                                    : 'Przejdź do głównej treści'}
                            </SkipLink>
                        </li>

                        <li>
                            <SkipLink href={`#${footerId}`}>
                                {env.NEXT_PUBLIC_APP_COUNTRY === 'RO'
                                    ? 'Mergi la subsol'
                                    : 'Przejdź do stopki'}
                            </SkipLink>
                        </li>
                    </ul>
                </nav>

                {promoBarData && (
                    <PromoBar<ImageProps>
                        promoBarData={promoBarData}
                        location={location}
                        promoBarClickEvent={() => {
                            bannerClickEvent();
                        }}
                        ImageComponent={Image}
                    />
                )}

                {mobileAppBarConfig && !minWidthMd && (
                    <MobileAppBar config={mobileAppBarConfig} />
                )}

                <Navigation config={navigationConfig} />
            </Header>

            <main
                id={mainId}
                tabIndex={-1}
                className="relative"
            >
                {children}
            </main>

            <Footer
                id={footerId}
                tabIndex={-1}
            />
        </>
    );
}
